/**
 * SetTableTablespacePopover — Designer-side popover wrapper for the Slice E2
 * tablespace / filegroup creation flow. Mirrors the per-popover pattern from
 * `PartitionPopovers.tsx`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E2.
 */
import { useState } from 'react';
import { Database } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import {
  getSetTableTablespaceUnsupportedTooltip,
  isSetTableTablespaceSupported,
} from '../../edit/edits/setTableTablespace';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { DisabledAffordance } from './DisabledAffordance';
import { SetTableTablespaceFormBody } from './SetTableTablespaceFormBody';

export interface SetTableTablespacePopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly defaultDatabase?: string | null;
  readonly onCommitted?: () => void;
}

export function SetTableTablespacePopover(props: SetTableTablespacePopoverProps) {
  const {
    editContext,
    testId = 'set-tablespace-popover',
    defaultDatabase,
    onCommitted,
  } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isSetTableTablespaceSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getSetTableTablespaceUnsupportedTooltip(engine)}
        variant="ghost"
        ariaLabel="Create tablespace or filegroup"
      >
        <Database className="h-3.5 w-3.5" />
        Set…
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Create tablespace or filegroup"
        >
          <Database className="h-3.5 w-3.5" />
          Set…
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <SetTableTablespaceFormBody
            editContext={editContext}
            testId={testId}
            defaultDatabase={defaultDatabase}
            onCommitted={() => { setOpen(false); onCommitted?.(); }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
